import { context, trace } from '@opentelemetry/api';
import { logger } from '@parrot-software/observability';

import type { StripeSessionDto } from '@subscriptions/application/dtos';
import type { IPaymentMethodApiPort } from '@subscriptions/application/ports';
import { extractErrorAttributes } from '@subscriptions/application/utils';
import type { PaymentSessionMode } from '@subscriptions/domain/types/SubscriptionsConst';

const paymentLog = logger.forDomain('SUB', { subdomain: 'billing' });
const tracer = trace.getTracer('@parrot-software/observability');

/**
 * Use case for creating a Stripe checkout session for a subscription block.
 *
 * Never logs `clientSecret` / `providerSessionId` (charter §5).
 *
 * @param port - Payment method API port
 * @returns Use case executor
 */
export const generateSessionUseCase = (port: IPaymentMethodApiPort) => {
  const execute = async (blockUuid: string, mode: PaymentSessionMode, isUpdate: boolean): Promise<StripeSessionDto> => {
    const span = tracer.startSpan('SUB.generateBlockPaymentSession', {
      attributes: { 'block.uuid': blockUuid, mode, isUpdate },
    });
    const ctx = trace.setSpan(context.active(), span);

    return context.with(ctx, async () => {
      try {
        const session = await port.generateSession(blockUuid, mode, isUpdate);
        paymentLog.info('generateBlockPaymentSession', { 'block.uuid': blockUuid, mode, isUpdate });
        return session;
      } catch (error) {
        paymentLog.error('generateBlockPaymentSession', { 'block.uuid': blockUuid, ...extractErrorAttributes(error) });
        throw error;
      } finally {
        span.end();
      }
    });
  };

  return { execute };
};
